import type { MedplumClient } from '@medplum/core';
import type { HealthcareService } from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirRestReadState,
  useFhirConnection,
  useFhirRestRead,
  useFhirSearch,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import { SERVICES_QUERY } from './fhirQueries';

/**
 * A "service" is a HealthcareService resource — one bookable session type.
 * Rendering belongs to the fhir-renderer view; this layer only moves
 * resources: list via FHIR GraphQL, detail via REST read, typeahead via
 * REST search, writes via the Medplum client.
 */

function mapServices(
  data: ResultOf<typeof SERVICES_QUERY>,
): HealthcareService[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is HealthcareService & { id: string } =>
      resource.resourceType === 'HealthcareService' && Boolean(resource.id),
  );
}

/** All org services, name-sorted by the server, paged. */
export function useServicesList(): FhirConnectionState<HealthcareService[]> {
  return useFhirConnection(
    SERVICES_QUERY,
    'HealthcareServiceConnection',
    mapServices,
  );
}

/** One service by id over REST — the read path for the detail page. */
export function useService(
  serviceId: string | undefined,
): FhirRestReadState<HealthcareService> {
  return useFhirRestRead(
    serviceId
      ? (client) => client.readResource('HealthcareService', serviceId)
      : null,
    [serviceId],
  );
}

/** Name typeahead over FHIR search; skipped until the query is 2+ chars. */
export function useServiceSearch(query: string): {
  resources: HealthcareService[];
  loading: boolean;
  active: boolean;
} {
  const trimmed = query.trim();
  const active = trimmed.length >= 2;
  const { resources, loading } = useFhirSearch(
    'HealthcareService',
    active ? { name: trimmed, _count: '20', _sort: 'name' } : null,
  );
  return { resources, loading, active };
}

export async function deleteService(
  client: MedplumClient,
  serviceId: string,
): Promise<void> {
  await client.deleteResource('HealthcareService', serviceId);
}
